import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Plus, Check, Loader2, Landmark, User, Phone, Mail, Hash, AtSign, Trash2 } from "lucide-react";
import {
  addPaymentDetails,
  deletePaymentDetails,
  GetBankDetailsByRoutingNumber,
  getPaymentdetailsByUserid,
  makeDefaultPaymentMethod,
} from "../../reducers/authentication.slice";
import { convertDateFormatInUtc } from "../../utils/util";
import { AuthContext } from "../../context/authContext";
import { getBankImageURL } from "../../utils/getBankImage";
import PaypalLogo from "../../assests/images/Paypal.png";
import PageHeader from "../../components/shared/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import useMediaQuery from "../../components/shared/useMediaQuery";

const EMPTY_FORM = {
  account_holder_name: "",
  phone_number: "",
  email: "",
  routing_number: "",
  account_number: "",
  paypal_username: "",
};

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const US_PHONE_REGEX = /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
const DIGITS_ONLY_KEYDOWN_BLOCK = ["e", "E", ".", ",", "+", "-"];

function blockNonDigitKeys(e) {
  if (
    DIGITS_ONLY_KEYDOWN_BLOCK.includes(e.key) ||
    (!/^\d$/.test(e.key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Home", "End"].includes(e.key) &&
      !e.ctrlKey &&
      !e.metaKey)
  ) {
    e.preventDefault();
  }
}

function blockNonDigitPaste(e) {
  const pasteData = e.clipboardData.getData("text");
  if (!/^\d+$/.test(pasteData)) e.preventDefault();
}

function maskSensitiveNumber(number) {
  if (!number) return "****";
  const lastFour = number.slice(-4);
  const maskedPortion = "*".repeat(Math.max(0, number.length - 4));
  return `${maskedPortion}${lastFour}`;
}

function IconInput({ icon: Icon, ...props }) {
  return (
    <div style={{ position: "relative", width: "100%", minWidth: 0 }}>
      <Icon
        size={15}
        style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#c3c8cc", pointerEvents: "none" }}
      />
      <Input
        style={{ width: "100%", minWidth: 0, boxSizing: "border-box", paddingLeft: 34, height: 38 }}
        {...props}
      />
    </div>
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return <div style={{ fontSize: 11.5, color: "#f27059", marginTop: 6 }}>{message}</div>;
}

export default function MemberPaymentMethodPage() {
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [open, setOpen] = useState(false);
  const [paymentType, setPaymentType] = useState("ACH");
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bankDetails, setBankDetails] = useState({});
  const [bankLookupLoading, setBankLookupLoading] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const getAllISF = () => {
    setLoading(true);
    dispatch(getPaymentdetailsByUserid()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setLoading(false);
      setPaymentDetails(res?.payload || []);
    });
  };

  useEffect(() => {
    getAllISF();
  }, []);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setPaymentType("ACH");
    setFormData(EMPTY_FORM);
    setErrors({});
    setBankDetails({});
  };

  const handlePaymentTypeChange = (value) => {
    setPaymentType(value);
    setFormData(EMPTY_FORM);
    setErrors({});
    setBankDetails({});
  };

  const handlePaymentsChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    if (value.trim() !== "") {
      if (name === "email" && !EMAIL_REGEX.test(value)) {
        setErrors((prev) => ({ ...prev, email: "Please enter a valid email address" }));
      } else if (name === "phone_number" && !US_PHONE_REGEX.test(value)) {
        setErrors((prev) => ({ ...prev, phone_number: "Please enter a valid US phone number" }));
      }
    }
  };

  const isFormValid = () => {
    if (paymentType === "ACH") {
      return (
        formData.account_holder_name?.trim() !== "" &&
        US_PHONE_REGEX.test(formData.phone_number) &&
        EMAIL_REGEX.test(formData.email) &&
        formData.routing_number?.trim() !== "" &&
        formData.account_number?.trim() !== ""
      );
    }
    if (paymentType === "PayPal") {
      return (
        formData.account_holder_name?.trim() !== "" &&
        EMAIL_REGEX.test(formData.email) &&
        formData.paypal_username?.trim() !== ""
      );
    }
    return false;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid()) return;
    setSubmitting(true);
    const payload = {
      payment_method: paymentType,
      ...formData,
      bank_name: bankDetails?.bank_name || "",
      is_primary: paymentDetails?.length < 1,
    };
    if (paymentType === "PayPal") delete payload.bank_name;

    dispatch(addPaymentDetails(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setSubmitting(false);
        showToast(res?.error?.message, "error");
        return;
      }
      showToast("Payment method added successfully", "success");
      setSubmitting(false);
      getAllISF();
      handleClose();
    });
  };

  const handleMakePrimary = (id) => {
    dispatch(makeDefaultPaymentMethod({ id })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      showToast("Payment method made primary successfully", "success");
      getAllISF();
    });
  };

  const handleDeleteConfirm = () => {
    dispatch(deletePaymentDetails({ id: deleteItem?.id })).then((res) => {
      setDeleteItem(null);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      showToast("Payment method deleted", "success");
      getAllISF();
    });
  };

  const handleGetBankDetails = (routingNumber) => {
    if (!routingNumber) return;
    setBankLookupLoading(true);
    dispatch(GetBankDetailsByRoutingNumber({ routing_number: routingNumber })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setBankLookupLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      if (res?.payload?.length === 0) {
        setBankLookupLoading(false);
        setBankDetails({});
        setErrors((prev) => ({ ...prev, routing_number: "We couldn't find a bank for this routing number." }));
        showToast("We couldn't find a bank for this routing number. Please double-check it before saving.", "warning");
        return;
      }
      setBankDetails(res?.payload[0]);
      setBankLookupLoading(false);
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <PageHeader
        title="Payment Methods"
        subtitle="Manage your saved payout methods."
        actions={
          <Button onClick={handleClickOpen}>
            <Plus size={15} style={{ marginRight: 6 }} />
            Add New Payment Method
          </Button>
        }
      />

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#c3c8cc" }}>
          <Loader2 size={20} className="tw-animate-spin" style={{ marginRight: 8 }} />
          Loading…
        </div>
      ) : paymentDetails?.length === 0 ? (
        <div
          style={{
            height: 200,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            border: "1px dashed rgba(255,255,255,0.12)",
            borderRadius: 14,
            color: "#c3c8cc",
            fontSize: 13,
          }}
        >
          <Landmark size={22} color="#c3c8cc" />
          No payment methods added yet.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "240px" : "320px"}, 1fr))`, gap: 16 }}>
          {paymentDetails.map((item, index) => {
            // Plaid logo (base64 PNG) first, then our own logo list.
            const bankImage = item?.bank_logo
              ? `data:image/png;base64,${item.bank_logo}`
              : item?.bank_name && getBankImageURL(item.bank_name);
            return (
              <div
                key={item?.id ?? index}
                style={{
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 16,
                  padding: 20,
                  background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
                  boxShadow: "0 16px 34px -26px rgba(0,0,0,0.7)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ display: "flex", gap: 14, alignItems: "flex-start", minWidth: 0 }}>
                    <div
                      style={{
                        width: 68,
                        height: 68,
                        borderRadius: 10,
                        background:
                          item.payment_method === "PayPal"
                            ? "linear-gradient(135deg, #010d83, #000531)"
                            : bankImage
                              ? "#fff"
                              : item?.bank_color || "#1e1e1e",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        padding: 6,
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      {item.payment_method === "PayPal" ? (
                        <img src={PaypalLogo} alt="PayPal" style={{ height: 26, objectFit: "contain" }} />
                      ) : bankImage ? (
                        <img src={bankImage} alt="Bank" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                      ) : (
                        <span style={{ fontSize: 11, fontWeight: 800, color: "#fff", textAlign: "center", lineHeight: 1.2, overflowWrap: "anywhere" }}>
                          {item?.bank_name?.split(",")[0] || "Bank"}
                        </span>
                      )}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 4 }}>
                        {item.payment_method === "ACH" && item?.bank_name
                          ? item.bank_name.split(",")[0]
                          : item.payment_method?.replace("_", " ")}
                      </div>
                      <div style={{ fontSize: 12.5, color: "#c3c8cc", fontFamily: "ui-monospace, SF Mono, Menlo, Fira Code, monospace", overflowWrap: "anywhere" }}>
                        {item.payment_method === "PayPal"
                          ? item.paypal_username || item.email
                          : item.account_last_four ? `****${item.account_last_four}` : maskSensitiveNumber(item.account_number)}
                      </div>
                    </div>
                  </div>

                  {item?.is_primary ? (
                    <div
                      title="Primary payment method"
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        background: "rgba(0,173,181,0.14)",
                    display: "flex",
                    alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Check size={15} color="#00ADB5" />
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
                      <button
                        type="button"
                        onClick={() => handleMakePrimary(item?.id)}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#2196F3",
                          fontSize: 12.5,
                          fontWeight: 600,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        Make Primary
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteItem(item)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          background: "transparent",
                          border: "none",
                          color: "#f27059",
                          fontSize: 12.5,
                          fontWeight: 600,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Trash2 size={13} />
                        Delete
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ fontSize: 11.5, color: "#c3c8cc", marginTop: 16 }}>
                  Added on {convertDateFormatInUtc(item?.created_at, "MM/DD/YYYY")}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
        <DialogContent style={{ maxWidth: 480 }} className="member-scroll">
          <DialogHeader>
            <DialogTitle>Add New Payment Method</DialogTitle>
            <DialogDescription>Select payment method first and enter the corresponding details.</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} style={{ width: "100%", minWidth: 0 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 20, flexDirection: isMobile ? "column" : "row", width: "100%", minWidth: 0 }}>
              {[
                { value: "ACH", label: "Bank Account" },
                { value: "PayPal", label: "PayPal" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handlePaymentTypeChange(opt.value)}
                  style={{
                    flex: isMobile ? "0 0 44px" : 1,
                    width: isMobile ? "100%" : undefined,
                    height: isMobile ? 44 : 38,
                    minHeight: isMobile ? 44 : undefined,
                    padding: isMobile ? "10px 12px" : undefined,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                    lineHeight: 1.2,
                    borderRadius: 9,
                    border: paymentType === opt.value ? "1px solid rgba(0,173,181,0.5)" : "1px solid rgba(255,255,255,0.1)",
                    background: paymentType === opt.value ? "rgba(0,173,181,0.14)" : "rgba(255,255,255,0.03)",
                    color: paymentType === opt.value ? "#00ADB5" : "#c3c8cc",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 18, width: "100%", minWidth: 0 }}>
              <div>
                <Label htmlFor="account_holder_name">Account Holder Name</Label>
                <IconInput
                  icon={User}
                  id="account_holder_name"
                  type="text"
                  name="account_holder_name"
                  value={formData.account_holder_name}
                  placeholder="Account Holder Name"
                  onChange={handlePaymentsChange}
                />
              </div>

              {paymentType === "ACH" ? (
                <>
                  <div>
                    <Label htmlFor="phone_number">Phone Number</Label>
                    <IconInput
                      icon={Phone}
                      id="phone_number"
                      type="text"
                      inputMode="numeric"
                      name="phone_number"
                      value={formData.phone_number}
                      placeholder="Phone Number"
                      onChange={handlePaymentsChange}
                      onKeyDown={blockNonDigitKeys}
                      onPaste={blockNonDigitPaste}
                    />
                    <FieldError message={errors.phone_number} />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <IconInput
                      icon={Mail}
                      id="email"
                      type="text"
                      name="email"
                      value={formData.email}
                      placeholder="Email Address"
                      onChange={handlePaymentsChange}
                    />
                    <FieldError message={errors.email} />
                  </div>
                  <div>
                    <Label htmlFor="routing_number">Routing Number</Label>
                    <IconInput
                      icon={Landmark}
                      id="routing_number"
                      type="text"
                      inputMode="numeric"
                      name="routing_number"
                      value={formData.routing_number}
                      placeholder="Routing Number"
                      onChange={handlePaymentsChange}
                      onKeyDown={blockNonDigitKeys}
                      onPaste={blockNonDigitPaste}
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/\D/g, "").slice(0, 9);
                      }}
                      onBlur={(e) => handleGetBankDetails(e.target.value)}
                    />
                    <FieldError message={errors.routing_number} />
                  </div>
                  <div>
                    <Label htmlFor="account_number">Account Number</Label>
                    <IconInput
                      icon={Hash}
                      id="account_number"
                      type="text"
                      inputMode="numeric"
                      name="account_number"
                      value={formData.account_number}
                      placeholder="Account Number"
                      onChange={handlePaymentsChange}
                      onKeyDown={blockNonDigitKeys}
                      onPaste={blockNonDigitPaste}
                    />
                  </div>

                  {bankLookupLoading && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#c3c8cc" }}>
                      <Loader2 size={13} className="tw-animate-spin" /> Looking up bank details…
                    </div>
                  )}

                  {bankDetails?.bank_name && (
                    <div
                      style={{
                        padding: 14,
                        borderRadius: 10,
                        border: "1px solid rgba(255,255,255,0.08)",
                        background: "rgba(255,255,255,0.02)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#00ADB5" }}>
                        Bank Information
                      </div>
                      <div style={{ fontSize: 12.5, color: "#c3c8cc" }}>
                        <span style={{ color: "#c3c8cc" }}>Bank Name: </span>
                        {bankDetails?.bank_name}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <IconInput
                      icon={Mail}
                      id="email"
                      type="text"
                      name="email"
                      value={formData.email}
                      placeholder="Email Address"
                      onChange={handlePaymentsChange}
                    />
                    <FieldError message={errors.email} />
                  </div>
                  <div>
                    <Label htmlFor="paypal_username">PayPal Username</Label>
                    <IconInput
                      icon={AtSign}
                      id="paypal_username"
                      type="text"
                      name="paypal_username"
                      value={formData.paypal_username}
                      placeholder="PayPal Username"
                      onChange={handlePaymentsChange}
                    />
                  </div>
                </>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24, flexWrap: "wrap" }}>
              <Button type="button" variant="outline" onClick={handleClose} style={{ width: isMobile ? "100%" : "auto" }}>
                Cancel
              </Button>
              <Button type="submit" disabled={!isFormValid() || submitting} style={{ width: isMobile ? "100%" : "auto" }}>
                {submitting ? <Loader2 size={14} className="tw-animate-spin" /> : "Submit"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteItem} onOpenChange={(v) => !v && setDeleteItem(null)}>
        <DialogContent style={{ maxWidth: 440 }}>
          <DialogHeader>
            <DialogTitle>Delete this payment method?</DialogTitle>
            <DialogDescription>
              {deleteItem?.payment_method === "PayPal"
                ? `PayPal (${deleteItem?.paypal_username || deleteItem?.email})`
                : `${deleteItem?.bank_name?.split(",")[0] || "Bank account"} ending in ${deleteItem?.account_last_four || deleteItem?.account_number?.slice(-4) || "****"}`}{" "}
              will be removed from your saved payment methods. Withdrawals you've already requested to this account will still be paid
              to it. If you need to change one, contact support@rs-chain.com.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteItem(null)} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
